import { useAuthStore } from "../store/authStore";
import { roleSlugFromToken } from "../lib/jwt";
import type { Permission } from "../lib/permissions";

/**
 * Whoever passes every check server-side, so the UI offers exactly what the
 * server would allow.
 *
 * The same rule as rbac.IsBypassRole: an org_role of owner or admin, OR a staff
 * role slug of owner or admin. Checking org_role alone hid actions from a
 * caller the server was happy to serve — the cost columns on Item Sales, for
 * one, which the API sends to anyone callerHolds() says may see them.
 */
function useBypassesPermissions(): boolean {
  const orgRole = useAuthStore((s) => s.user?.org_role);
  const token = useAuthStore((s) => s.token);
  if (orgRole === "owner" || orgRole === "admin") {
    return true;
  }
  const slug = roleSlugFromToken(token);
  return slug === "owner" || slug === "admin";
}

/**
 * Hook to check if the current user has a specific permission.
 *
 * Mirrors server-side RBAC enforcement exactly:
 *  - org owner/admin, and a staff role slug of owner/admin, bypass all checks
 *    (rbac.IsBypassRole),
 *  - everyone else is checked against the `permissions[]` array resolved
 *    server-side and stored from `GET /auth/session`.
 *
 * Roles with no catalog entry (e.g. accountant/viewer) resolve to an empty
 * array server-side, so they grant no gated actions until their templates are
 * seeded server-side.
 */
export function usePermission(permission: Permission): boolean {
  const bypasses = useBypassesPermissions();
  const permissions = useAuthStore((s) => s.permissions);

  if (bypasses) {
    return true;
  }

  return permissions.includes(permission);
}

/**
 * Hook to check if user has ANY of the specified permissions.
 */
export function useAnyPermission(permissions: Permission[]): boolean {
  const bypasses = useBypassesPermissions();
  const granted = useAuthStore((s) => s.permissions);

  if (bypasses) {
    return true;
  }

  return permissions.some((p) => granted.includes(p));
}

/**
 * Hook to check if user has ALL of the specified permissions.
 */
export function useAllPermissions(permissions: Permission[]): boolean {
  const bypasses = useBypassesPermissions();
  const granted = useAuthStore((s) => s.permissions);

  if (bypasses) {
    return true;
  }

  return permissions.every((p) => granted.includes(p));
}
